import { useEffect, useState } from "react";
import api, { fmtMXN } from "@/lib/api";
import { calcISR, periodDays } from "@/lib/isr";
import { X, Zap, CheckSquare, Square, Banknote, Printer } from "lucide-react";

export default function BatchPayrollModal({ projects, employees, onClose, onDone }) {
  const obraEmployees = employees.filter((e) => e.payroll_type === "obra" && e.active !== false);
  const projectIdsWithObra = [...new Set(obraEmployees.map((e) => e.project_id))];
  const projectsWithObra = projects.filter((p) => projectIdsWithObra.includes(p.id));

  const [projectId, setProjectId] = useState(projectsWithObra[0]?.id || "");
  // Default to last 7 days
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 6 * 86400000);
  const [start, setStart] = useState(weekAgo.toISOString().slice(0, 10));
  const [end, setEnd] = useState(today.toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [showCash, setShowCash] = useState(false);

  const calculate = async () => {
    if (!projectId || !start || !end) return;
    setLoading(true);
    setDone(null);
    try {
      const empsInProject = obraEmployees.filter((e) => e.project_id === projectId);
      let balances = {};
      try {
        const { data } = await api.get("/employee-loans/balances");
        balances = data || {};
      } catch { /* sin permiso o error: sin prellenado */ }
      const summaries = await Promise.all(
        empsInProject.map((e) =>
          api.get("/attendance/summary", { params: { employee_id: e.id, start, end } })
            .then((r) => r.data)
            .catch(() => ({ days: 0, total_hours: 0, total_bonus: 0, records: 0 }))
        )
      );
      const newRows = empsInProject.map((emp, i) => {
        const s = summaries[i];
        const dailyRate = Number(emp.base_salary || 0);
        const calcGross = Math.round((s.total_hours / 8) * dailyRate * 100) / 100;
        return {
          employee_id: emp.id,
          employee_name: emp.name,
          position: emp.position,
          tax_regime: emp.tax_regime || null,
          daily_rate: dailyRate,
          days: s.days,
          hours: s.total_hours,
          has_attendance: s.records > 0,
          gross: calcGross,
          bonuses: s.total_bonus,
          deductions: balances[emp.id]?.suggested || 0,
          loan_balance: balances[emp.id]?.balance || 0,
          selected: s.records > 0,
          status: "pending",
        };
      });
      setRows(newRows);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { calculate(); /* eslint-disable-line */ }, [projectId, start, end]);

  const update = (idx, key, value) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };

  const updateDays = (idx, value) => {
    setRows((rs) => rs.map((r, i) => {
      if (i !== idx) return r;
      const days = value === "" ? "" : Number(value);
      const gross = days === "" ? 0 : Math.round(days * r.daily_rate * 100) / 100;
      const payable = days !== "" && days > 0;
      return { ...r, days: value, gross, selected: payable ? true : r.hours > 0 && r.selected };
    }));
  };

  const toggleAll = () => {
    const anySelected = rows.some((r) => r.selected);
    setRows((rs) => rs.map((r) => ({ ...r, selected: !anySelected && (r.hours > 0 || Number(r.days) > 0) })));
  };

  const rowIsr = (r) => {
    if (r.tax_regime !== "asimilado" || !start || !end) return 0;
    return calcISR(Number(r.gross || 0) + Number(r.bonuses || 0), periodDays(start, end));
  };

  const totals = rows.reduce(
    (acc, r) => {
      if (!r.selected) return acc;
      const net = Number(r.gross) + Number(r.bonuses) - Number(r.deductions) - rowIsr(r);
      acc.gross += Number(r.gross || 0);
      acc.bonuses += Number(r.bonuses || 0);
      acc.deductions += Number(r.deductions || 0);
      acc.net += net;
      acc.count += 1;
      return acc;
    },
    { gross: 0, bonuses: 0, deductions: 0, net: 0, count: 0 }
  );

  const submit = async () => {
    setSubmitting(true);
    const selected = rows.filter((r) => r.selected);
    let ok = 0, fail = 0;
    for (let i = 0; i < rows.length; i++) {
      if (!rows[i].selected) continue;
      try {
        await api.post("/payroll", {
          employee_id: rows[i].employee_id,
          period_start: start,
          period_end: end,
          gross: Number(rows[i].gross),
          bonuses: Number(rows[i].bonuses || 0),
          deductions: Number(rows[i].deductions || 0),
          loan_deduction: Math.min(Number(rows[i].deductions || 0), rows[i].loan_balance || 0),
        });
        update(i, "status", "ok");
        ok += 1;
      } catch {
        update(i, "status", "fail");
        fail += 1;
      }
    }
    setDone({ ok, fail, total: selected.length });
    setSubmitting(false);
  };

  const close = () => {
    if (done && done.ok > 0) onDone();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={close}>
      <div className="panel-strong w-full max-w-5xl max-h-[92vh] overflow-y-auto scan-line" onClick={(e) => e.stopPropagation()}>
        <div className="panel-header px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <div className="label-mono">// LISTA DE RAYA</div>
            <h2 className="font-display font-bold text-xl text-slate-100 tracking-wide flex items-center gap-2">
              <Zap className="w-5 h-5" style={{ color: "var(--magenta)" }} /> Nómina semanal masiva
            </h2>
          </div>
          <button onClick={close} className="text-slate-500 hover:text-cyan-300"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label-mono block mb-1.5">Proyecto</label>
              <select data-testid="batch-project" value={projectId} onChange={(e) => setProjectId(e.target.value)}
                className="w-full input-cyber px-3 py-2 text-sm">
                {projectsWithObra.length === 0 && <option value="" style={{ background: "#0a0f1d" }}>Sin obreros asignados</option>}
                {projectsWithObra.map((p) => (
                  <option key={p.id} value={p.id} style={{ background: "#0a0f1d" }}>{p.code} - {p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Inicio período</label>
              <input type="date" data-testid="batch-start" value={start} onChange={(e) => setStart(e.target.value)}
                className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Fin período</label>
              <input type="date" data-testid="batch-end" value={end} onChange={(e) => setEnd(e.target.value)}
                className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
          </div>

          <div
            className="px-3 py-2.5 rounded-sm text-[11px] text-slate-400"
            style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.2)" }}
            data-testid="batch-manual-hint"
          >
            💡 <b className="text-slate-200">Nóminas históricas:</b> si el período no tiene asistencias registradas (ej. antes de usar el sistema),
            escribe los <b className="text-cyan-300">días trabajados</b> directamente en la columna "Días" — el bruto se calcula solo (días × salario diario).
          </div>

          <div className="panel overflow-hidden">
            <table className="w-full text-sm">
              <thead className="panel-header">
                <tr className="text-left">
                  <th className="px-3 py-2.5 w-10">
                    <button onClick={toggleAll} data-testid="batch-toggle-all" className="text-slate-300 hover:text-cyan-300">
                      {rows.some(r => r.selected) ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                    </button>
                  </th>
                  <th className="label-mono px-3 py-2.5">Empleado</th>
                  <th className="label-mono px-3 py-2.5 text-right">Días</th>
                  <th className="label-mono px-3 py-2.5 text-right">Horas</th>
                  <th className="label-mono px-3 py-2.5 text-right">Bruto</th>
                  <th className="label-mono px-3 py-2.5 text-right">Bonos</th>
                  <th className="label-mono px-3 py-2.5 text-right">Deducc.</th>
                  <th className="label-mono px-3 py-2.5 text-right">Neto</th>
                  <th className="label-mono px-3 py-2.5 w-20">Estado</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={9} className="text-center py-8 text-slate-400 label-mono">Calculando…</td></tr>}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-8 text-slate-500">Sin obreros en este proyecto.</td></tr>
                )}
                {!loading && rows.map((r, i) => {
                  const isr = rowIsr(r);
                  const net = Number(r.gross || 0) + Number(r.bonuses || 0) - Number(r.deductions || 0) - isr;
                  const payable = r.hours > 0 || Number(r.days) > 0;
                  return (
                    <tr key={r.employee_id} data-testid={`batch-row-${r.employee_id}`}
                      className="row-hover" style={{ borderBottom: "1px solid var(--border)", opacity: payable ? 1 : 0.55 }}>
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => update(i, "selected", !r.selected)} disabled={!payable || r.status === "ok"}
                          data-testid={`batch-check-${r.employee_id}`}
                          className="text-slate-300 hover:text-cyan-300 disabled:opacity-30 disabled:cursor-not-allowed">
                          {r.selected ? <CheckSquare className="w-4 h-4" style={{ color: "var(--cyan)" }} /> : <Square className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-slate-100 text-sm">{r.employee_name}</div>
                        <div className="text-xs text-slate-500">{r.position} · {fmtMXN(r.daily_rate)}/día</div>
                        {r.loan_balance > 0 && (
                          <div className="text-[10px] font-mono" style={{ color: "#fbbf24" }} data-testid={`batch-loan-${r.employee_id}`}>
                            💰 Préstamos/cargos pendientes: {fmtMXN(r.loan_balance)} (prellenado en deducc.)
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" min="0" step="0.5" value={r.days} onChange={(e) => updateDays(i, e.target.value)} disabled={r.status === "ok"}
                          data-testid={`batch-days-${r.employee_id}`}
                          className="w-16 input-cyber px-2 py-1 text-xs text-right tabular-nums"
                          style={!r.has_attendance && Number(r.days) > 0 ? { borderColor: "var(--cyan)" } : undefined} />
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-300">{r.hours}</td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" value={r.gross} onChange={(e) => update(i, "gross", e.target.value)} disabled={r.status === "ok"}
                          className="w-24 input-cyber px-2 py-1 text-xs text-right tabular-nums" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" value={r.bonuses} onChange={(e) => update(i, "bonuses", e.target.value)} disabled={r.status === "ok"}
                          className="w-20 input-cyber px-2 py-1 text-xs text-right tabular-nums" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" value={r.deductions} onChange={(e) => update(i, "deductions", e.target.value)} disabled={r.status === "ok"}
                          className="w-20 input-cyber px-2 py-1 text-xs text-right tabular-nums" />
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-cyan-300">
                        {fmtMXN(net)}
                        {isr > 0 && <div className="text-[9px] font-normal" style={{ color: "#fbbf24" }}>ISR -{fmtMXN(isr)}</div>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {r.status === "ok" && <span className="px-2 py-0.5 rounded-sm text-[10px] font-semibold badge-success">PAGADO</span>}
                        {r.status === "fail" && <span className="px-2 py-0.5 rounded-sm text-[10px] font-semibold badge-danger">ERROR</span>}
                        {r.status === "pending" && !payable && <span className="text-[10px] text-slate-500 label-mono">Sin asistencia</span>}
                        {r.status === "pending" && payable && !r.has_attendance && <span className="text-[10px] label-mono" style={{ color: "var(--cyan)" }}>Manual</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {!loading && rows.length > 0 && (
                <tfoot>
                  <tr style={{ background: "var(--bg-2)" }}>
                    <td colSpan={4} className="px-3 py-3 text-right label-mono">TOTAL ({totals.count} pagos):</td>
                    <td className="px-3 py-3 text-right font-mono text-slate-200">{fmtMXN(totals.gross)}</td>
                    <td className="px-3 py-3 text-right font-mono" style={{ color: "var(--green)" }}>+{fmtMXN(totals.bonuses)}</td>
                    <td className="px-3 py-3 text-right font-mono" style={{ color: "var(--red)" }}>-{fmtMXN(totals.deductions)}</td>
                    <td className="px-3 py-3 text-right font-mono font-bold" style={{ color: "var(--cyan)", textShadow: "0 0 10px var(--cyan-glow)" }}>{fmtMXN(totals.net)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {done && (
            <div className={`px-3 py-2.5 rounded-sm text-sm ${done.fail === 0 ? "badge-success" : "badge-pending"}`}>
              ✓ {done.ok} de {done.total} pagos generados {done.fail > 0 && `· ${done.fail} con error`}
            </div>
          )}

          <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
            {totals.count > 0 && (
              <button type="button" onClick={() => setShowCash(true)} data-testid="cash-breakdown-btn"
                className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2 mr-auto"
                style={{ color: "var(--green)", borderColor: "var(--green)" }}>
                <Banknote className="w-4 h-4" /> Desglose efectivo
              </button>
            )}
            <button type="button" onClick={close} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">
              {done ? "Cerrar" : "Cancelar"}
            </button>
            {!done && (
              <button type="button" onClick={submit} disabled={submitting || totals.count === 0}
                data-testid="batch-submit-btn"
                className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2 disabled:opacity-50">
                <Zap className="w-4 h-4" /> {submitting ? "Generando…" : `Generar ${totals.count} pagos · ${fmtMXN(totals.net)}`}
              </button>
            )}
          </div>
        </div>
      </div>
      {showCash && <CashBreakdownModal rows={rows.filter((r) => r.selected)} onClose={() => setShowCash(false)} />}
    </div>
  );
}

const DENOMS = [1000, 500, 200, 100, 50, 20, 10, 5, 2, 1];

function CashBreakdownModal({ rows, onClose }) {
  const workers = rows.map((r) => {
    const net = Math.round(Number(r.gross || 0) + Number(r.bonuses || 0) - Number(r.deductions || 0));
    let rem = net;
    const breakdown = {};
    for (const d of DENOMS) {
      const n = Math.floor(rem / d);
      if (n > 0) { breakdown[d] = n; rem -= n * d; }
    }
    return { name: r.employee_name, net, breakdown };
  });
  const totalDenoms = {};
  for (const w of workers) {
    for (const [d, n] of Object.entries(w.breakdown)) totalDenoms[d] = (totalDenoms[d] || 0) + n;
  }
  const grandTotal = workers.reduce((a, w) => a + w.net, 0);

  const printIt = () => {
    const el = document.getElementById("cash-breakdown-print");
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    doc.title = "Desglose de efectivo";
    const style = doc.createElement("style");
    style.textContent = `body{font-family:monospace;color:#111;padding:24px}
      table{border-collapse:collapse;width:100%;margin-bottom:16px}
      th,td{border:1px solid #999;padding:4px 8px;font-size:12px;text-align:right}
      th:first-child,td:first-child{text-align:left}
      h2{font-size:16px} h3{font-size:13px;margin:12px 0 4px}`;
    doc.head.appendChild(style);
    doc.body.innerHTML = el.innerHTML;
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.9)", backdropFilter: "blur(6px)" }} onClick={onClose} data-testid="cash-breakdown-modal">
      <div className="w-full max-w-2xl panel-strong flex flex-col" style={{ maxHeight: "90vh" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="label-mono">// DESGLOSE DE EFECTIVO PARA LA RAYA</div>
            <div className="text-xs text-slate-400 mt-0.5">{workers.length} trabajadores · total {fmtMXN(grandTotal)} (montos redondeados a pesos)</div>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={printIt} data-testid="cash-print-btn" className="btn-ghost px-2.5 py-1.5 rounded-sm text-[10px] uppercase tracking-widest flex items-center gap-1.5" style={{ color: "var(--cyan)" }}>
              <Printer className="w-3.5 h-3.5" /> Imprimir
            </button>
            <button onClick={onClose} className="btn-ghost p-1.5 rounded-sm"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="overflow-y-auto p-5" id="cash-breakdown-print">
          <h2 className="hidden">Desglose de efectivo · Total ${grandTotal.toLocaleString()}</h2>
          <h3 className="label-mono mb-2" style={{ color: "var(--green)" }}>// BILLETES Y MONEDAS PARA EL BANCO</h3>
          <table className="w-full text-xs mb-5">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-2 py-1.5">Denominación</th><th className="px-2 py-1.5 text-right">Cantidad</th><th className="px-2 py-1.5 text-right">Importe</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
              {DENOMS.filter((d) => totalDenoms[d]).map((d) => (
                <tr key={d}>
                  <td className="px-2 py-1.5 font-mono text-slate-200">${d.toLocaleString()}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-slate-200">× {totalDenoms[d]}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-bold" style={{ color: "var(--green)" }}>{fmtMXN(d * totalDenoms[d])}</td>
                </tr>
              ))}
              <tr>
                <td className="px-2 py-2 font-bold uppercase text-slate-100" colSpan={2}>Total a retirar</td>
                <td className="px-2 py-2 text-right font-mono font-bold text-base" style={{ color: "var(--green)" }}>{fmtMXN(grandTotal)}</td>
              </tr>
            </tbody>
          </table>
          <h3 className="label-mono mb-2" style={{ color: "var(--cyan)" }}>// SOBRE POR TRABAJADOR</h3>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-2 py-1.5">Trabajador</th><th className="px-2 py-1.5 text-right">Neto</th><th className="px-2 py-1.5 text-right">Desglose</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
              {workers.map((w) => (
                <tr key={w.name} data-testid={`cash-worker-${w.name}`}>
                  <td className="px-2 py-1.5 text-slate-200">{w.name}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-bold text-slate-100">{fmtMXN(w.net)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-slate-400">
                    {DENOMS.filter((d) => w.breakdown[d]).map((d) => `${w.breakdown[d]}×$${d}`).join(" + ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
